import React, { useState, useEffect } from "react"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import Grid from "@mui/material/Grid"
import Tabs from "@mui/material/Tabs"
import Tab from "@mui/material/Tab"
import CircularProgress from "@mui/material/CircularProgress"
import PropertyCard from "../PropertyCard"
import SecondaryPropertyCard from "../SecondaryPropertyCard"
import ShortTermRentalCard from "../ShortTermRentalCard"
import ListItem from "@mui/material/ListItem"
import { getListings, getImageUrl } from "../../../../../services/listings"

const Main = () => {
    const [tabValue, setTabValue] = useState(0)
    const [activeSalesListings, setActiveSalesListings] = useState([])
    const [rentalListings, setRentalListings] = useState([])
    const [shortTermRentals, setShortTermRentals] = useState([])
    const [pastSales, setPastSales] = useState([])
    const [pastRentals, setPastRentals] = useState([])
    const [loading, setLoading] = useState(true)

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue)
    }

    useEffect(() => {
        const fetchAllListings = async () => {
            setLoading(true)
            try {
                // Fetch all listing types in parallel
                const [activeSales, rentals, shortTerm, pastSalesData, pastRentalsData] = await Promise.all([
                    getListings('active-sales'),
                    getListings('rentals'),
                    getListings('short-term-rentals'),
                    getListings('past-sales'),
                    getListings('past-rentals'),
                ])

                // Transform active sales listings - convert image IDs to URLs
                const transformedActiveSales = activeSales.map((listing) => ({
                    ...listing,
                    images: listing.images && listing.images.length > 0
                        ? listing.images.map((img) => {
                            // img is now just an ObjectId string (no populate)
                            const imageId = typeof img === 'string' ? img : (img._id || img);
                            return getImageUrl(imageId);
                        })
                        : [],
                }))

                // Transform rental listings - convert image IDs to URLs
                const transformedRentals = rentals.map((listing) => ({
                    ...listing,
                    images: listing.images && listing.images.length > 0
                        ? listing.images.map((img) => {
                            // img is now just an ObjectId string (no populate)
                            const imageId = typeof img === 'string' ? img : (img._id || img);
                            return getImageUrl(imageId);
                        })
                        : [],
                }))

                // Transform short-term rentals - convert image IDs to URLs
                const transformedShortTerm = shortTerm.map((listing) => ({
                    ...listing,
                    images: listing.images && listing.images.length > 0
                        ? listing.images.map((img) => {
                            // img is now just an ObjectId string (no populate)
                            const imageId = typeof img === 'string' ? img : (img._id || img);
                            return getImageUrl(imageId);
                        })
                        : [],
                }))

                // Transform past sales - convert single image ID to URL
                const transformedPastSales = pastSalesData.map((listing) => ({
                    ...listing,
                    image: listing.image
                        ? (() => {
                            // image is now just an ObjectId string (no populate)
                            const imageId = typeof listing.image === 'string' ? listing.image : (listing.image._id || listing.image);
                            return getImageUrl(imageId);
                        })()
                        : null,
                }))

                // Transform past rentals - convert single image ID to URL
                const transformedPastRentals = pastRentalsData.map((listing) => ({
                    ...listing,
                    image: listing.image
                        ? (() => {
                            // image is now just an ObjectId string (no populate)
                            const imageId = typeof listing.image === 'string' ? listing.image : (listing.image._id || listing.image);
                            return getImageUrl(imageId);
                        })()
                        : null,
                }))

                setActiveSalesListings(transformedActiveSales)
                setRentalListings(transformedRentals)
                setShortTermRentals(transformedShortTerm)
                setPastSales(transformedPastSales)
                setPastRentals(transformedPastRentals)
            } catch (error) {
                console.error('Error fetching listings:', error)
                // On error, set empty arrays so the page still renders
                setActiveSalesListings([])
                setRentalListings([])
                setShortTermRentals([])
                setPastSales([])
                setPastRentals([])
            } finally {
                setLoading(false)
            }
        }

        fetchAllListings()
    }, [])

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        )
    }

    const renderListings = (listings, listingType = null) => {
        if (!listings || listings.length === 0) {
            const message = listingType === 'active-sales' 
                ? 'No active sales at this time' 
                : 'No active rentals at this time';
            
            return (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: 1,
                    }}
                >
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        align="center"
                    >
                        {message}
                    </Typography>
                </Box>
            );
        }

        return (
            <Grid container spacing={4}>
                {listings.map((item, i) => (
                    <Grid item xs={12} md={6} lg={4} key={i} data-aos={"fade-up"}>
                        <Box
                            sx={{
                                textDecoration: "none",
                                transition: "all .2s ease-in-out",
                            }}
                        >
                            <PropertyCard
                                images={item.images || []}
                                price={item.price}
                                beds={item.beds}
                                baths={item.baths}
                                sqft={item.sqft}
                                acres={item.acres}
                                address={item.address}
                                status={item.status}
                                url={item.url}
                                listingType={listingType}
                            />
                        </Box>
                    </Grid>
                ))}
            </Grid>
        );
    }

    const renderShortTermRentals = (listings) => {
        if (!listings || listings.length === 0) {
            return (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: 1,
                    }}
                >
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        align="center"
                    >
                        No short term rentals at this time
                    </Typography>
                </Box>
            );
        }

        return (
            <Grid container spacing={4}>
                {listings.map((item, i) => (
                    <Grid item xs={12} md={6} lg={4} key={i} data-aos={"fade-up"}>
                        <Box
                            sx={{
                                textDecoration: "none",
                                transition: "all .2s ease-in-out",
                            }}
                        >
                            <ShortTermRentalCard
                                images={item.images || []}
                                title={item.title}
                                subtitle={item.subtitle}
                                rating={item.rating}
                                reviewsCount={item.reviewsCount}
                                bedrooms={item.bedrooms}
                                beds={item.beds}
                                baths={item.baths}
                                guests={item.guests}
                                location={item.location}
                                status={item.status}
                                url={item.url}
                            />
                        </Box>
                    </Grid>
                ))}
            </Grid>
        );
    }

    const renderPastListings = (listings, listingType) => (
        <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
            {listings.map((item, i) => (
                <Grid item xs={12} md={6} lg={4} key={i} sx={{ display: "flex" }}>
                    <ListItem
                        sx={{
                            padding: "8px 0",
                            width: "100%",
                            display: "flex",
                            "&:not(:last-child)": {
                                borderBottom: "1px solid",
                                borderColor: "divider",
                            },
                        }}
                        data-aos="fade-up"
                    >
                        <Box width="100%" sx={{ display: "flex", height: "100%" }}>
                            <SecondaryPropertyCard
                                image={item.image || ""}
                                price={item.price}
                                beds={item.beds}
                                baths={item.baths}
                                sqft={item.sqft}
                                acres={item.acres}
                                hoa={item.hoa}
                                address={item.address}
                                url={item.url}
                                listingType={listingType}
                            />
                        </Box>
                    </ListItem>
                </Grid>
            ))}
        </Grid>
    )

    return (
        <Box>
            <Box marginBottom={4}>
                <Typography
                    sx={{
                        textTransform: "uppercase",
                        fontWeight: "medium",
                    }}
                    gutterBottom
                    color={"secondary"}
                    align={"center"}
                >
                    Featured Listings
                </Typography>
                <Typography
                    fontWeight={700}
                    variant={"h3"}
                    gutterBottom
                    align={"center"}
                    data-aos={"fade-up"}
                >
                    Discover Your Dream Home in San Diego&apos;s Finest
                    Neighborhoods
                </Typography>
                <Typography
                    variant={"h6"}
                    component={"p"}
                    color={"textSecondary"}
                    align={"center"}
                    data-aos={"fade-up"}
                >
                    Explore our curated selection of premium properties, from
                    cozy family homes to luxurious estates, tailored to meet
                    your unique lifestyle needs.
                </Typography>
            </Box>

            <Box
                sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    marginBottom: 4,
                }}
            >
                <Tabs value={tabValue} onChange={handleTabChange} centered>
                    <Tab label="For Sale" />
                    <Tab label="For Rent" />
                    <Tab label="Short Term Rentals" />
                </Tabs>
            </Box>

            {tabValue === 0 && renderListings(activeSalesListings, 'active-sales')}
            {tabValue === 1 && renderListings(rentalListings, 'rentals')}
            {tabValue === 2 && renderShortTermRentals(shortTermRentals)}

            <Box marginTop={8} marginBottom={4}>
                <Typography
                    variant={"h4"}
                    fontWeight={700}
                    gutterBottom
                    align={"center"}
                    data-aos={"fade-up"}
                >
                    Past Transactions
                </Typography>
            </Box>
            <Box marginTop={6} marginBottom={4}>
                <Typography
                    variant={"h6"}
                    fontWeight={700}
                    gutterBottom
                    align={"start"}
                    data-aos={"fade-up"}
                >
                    Past Sales
                </Typography>
            </Box>
            {renderPastListings(pastSales, 'past-sales')}

            <Box marginTop={6} marginBottom={4}>
                <Typography
                    variant={"h6"}
                    fontWeight={700}
                    gutterBottom
                    align={"start"}
                    data-aos={"fade-up"}
                >
                    Past Rentals
                </Typography>
            </Box>
            {renderPastListings(pastRentals, 'past-rentals')}
        </Box>
    )
}

export default Main
